import React from 'react';
import { AVAILABLE_AREAS } from './constants';

// Helper function to get role colors
const getRoleColors = (role) => {
  const roleColors = {
    'ADMIN': 'bg-red-100 text-red-800',
    'MANAGER': 'bg-purple-100 text-purple-800',
    'GUEST_APP_MANAGER': 'bg-indigo-100 text-indigo-800',
    'PROPERTY_MANAGER': 'bg-blue-100 text-blue-800',
    'HOUSEKEEPER_MANAGER': 'bg-green-100 text-green-800',
    'HOUSEKEEPER': 'bg-emerald-100 text-emerald-800',
    'INSPECTOR': 'bg-orange-100 text-orange-800',
    'MAINTENANCE': 'bg-yellow-100 text-yellow-800'
  };
  return roleColors[role] || 'bg-gray-100 text-gray-800';
};

export default function TeamMemberRow({
  member,
  activeTab,
  onEdit,
  onToggleActive,
  onResendInvite,
  isResendingInvite,
  resendingMemberId
}) {
  return (
    <tr key={member.id}>
      <td className="px-2 sm:px-3 py-4">
        <div className="flex items-center">
          <div className="hidden sm:flex h-8 w-8 flex-shrink-0">
            <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-sm font-medium">
              {`${member.firstName?.[0] || ''}${member.lastName?.[0] || ''}`}
            </span>
          </div>
          <div className="sm:ml-3 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {member.firstName && member.lastName 
                ? `${member.firstName} ${member.lastName}`
                : member.name}
            </div>
            <div className="sm:hidden text-xs text-gray-500 truncate">
              {member.email}
            </div>
            <div className="sm:hidden mt-1 space-y-1">
              <div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColors(member.role)}`}>
                  {member.role}
                </span>
              </div>
              {/* Mobile team display */}
              {member.userTeams && member.userTeams.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {member.userTeams.slice(0, 2).map((userTeam) => (
                    <div
                      key={userTeam.id}
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border"
                      style={{
                        backgroundColor: userTeam.team?.color ? `${userTeam.team.color}20` : '#E5E7EB',
                        borderColor: userTeam.team?.color || '#9CA3AF',
                        color: userTeam.team?.color || '#374151'
                      }}
                    >
                      <div
                        className="w-1.5 h-1.5 rounded-full mr-1 flex-shrink-0"
                        style={{ backgroundColor: userTeam.team?.color || '#6B7280' }}
                      />
                      {userTeam.team?.name}
                      {userTeam.isPrimary && (
                        <span className="ml-0.5 text-xs font-bold" title="Primary Team">
                          ★
                        </span>
                      )}
                    </div>
                  ))}
                  {member.userTeams.length > 2 && (
                    <span className="text-xs text-gray-500">
                      +{member.userTeams.length - 2} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="hidden md:table-cell px-2 sm:px-3 py-4 whitespace-nowrap">
        <div className="text-xs text-gray-900">{member.role}</div>
      </td>
      <td className="hidden md:table-cell px-3 py-4" style={{ minWidth: '200px' }}>
        <div className="text-sm text-gray-900">
          {member.areas && member.areas.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {member.areas.map(area => (
                <span key={area} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                  {AVAILABLE_AREAS.find(a => a.value === area)?.label || area}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-500 text-xs">No areas</span>
          )}
        </div>
      </td>
      <td className="hidden lg:table-cell px-3 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">
          {/* Multi-team display with colored bubbles */}
          {member.userTeams && member.userTeams.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {member.userTeams.map((userTeam) => (
                <div
                  key={userTeam.id}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                  style={{
                    backgroundColor: userTeam.team?.color ? `${userTeam.team.color}20` : '#E5E7EB',
                    borderColor: userTeam.team?.color || '#9CA3AF',
                    color: userTeam.team?.color || '#374151'
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full mr-1.5 flex-shrink-0"
                    style={{ backgroundColor: userTeam.team?.color || '#6B7280' }}
                  />
                  {userTeam.team?.name}
                  {userTeam.isPrimary && (
                    <span className="ml-1 text-xs font-bold" title="Primary Team">
                      ★
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : member.team ? (
            /* Fallback to legacy single team display */
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              {member.team.name}
            </span>
          ) : (
            <span className="text-gray-500 text-xs">No teams</span>
          )}
        </div>
      </td>
      <td className="hidden sm:table-cell px-2 sm:px-3 py-4 whitespace-nowrap">
        {activeTab === 'active' ? (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Active
          </span>
        ) : (
          <button 
            onClick={() => onToggleActive(member)}
            className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
            aria-label="Activate user"
          >
            Reactivate
          </button>
        )}
      </td>
      <td className="px-2 sm:px-3 py-4 text-right text-sm font-medium">
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button 
            onClick={() => onEdit(member)}
            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md text-sky-700 bg-sky-50 hover:bg-sky-100 border border-sky-200 hover:border-sky-300 transition-colors min-h-[32px]"
          >
            Edit
          </button>
          
          <button 
            onClick={() => onResendInvite(member)}
            disabled={isResendingInvite && resendingMemberId === member.id}
            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 hover:border-orange-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed transition-colors min-h-[32px]"
          >
            {isResendingInvite && resendingMemberId === member.id ? 'Sending...' : 'Resend'}
          </button>
        </div>
      </td>
    </tr>
  );
} 